import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ArrowBack,
    Code,
    Image as ImageIcon,
    CheckCircle,
    Error as ErrorIcon,
} from '@mui/icons-material';

import PageContainer from '../../../components/PageContainer';
import { getModelFrontLogById } from '../../../store/thunk/modelFrontLogs';
import { IStore } from '../../../store/types';

function PerfCard({ label, value, color }: { label: string; value?: number; color?: string }) {
    const theme = useTheme();
    return (
        <Box sx={{
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
        }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: color || theme.palette.text.primary }}>
                {value !== undefined ? `${value.toFixed(1)} ms` : '—'}
            </Typography>
        </Box>
    );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="body2" sx={{ minWidth: 160, color: theme.palette.text.secondary, flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                {value}
            </Typography>
        </Box>
    );
}

function ScoreBar({ score }: { score: number }) {
    const color = score >= 0.9 ? '#4caf50' : score >= 0.8 ? '#ff9800' : '#f44336';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 80, height: 8, bgcolor: 'action.hover', borderRadius: 4, overflow: 'hidden' }}>
                <Box sx={{ width: `${score * 100}%`, height: '100%', bgcolor: color, borderRadius: 4 }} />
            </Box>
            <Typography variant="body2" sx={{ color, fontWeight: 'bold', minWidth: 44 }}>
                {(score * 100).toFixed(2)}%
            </Typography>
        </Box>
    );
}

const OneModelFrontLog = () => {
    const { logId } = useParams<{ logId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const [rawOpen, setRawOpen] = useState(false);
    const [imageOpen, setImageOpen] = useState(false);

    const { singleLog: log, singleLogStatus: status, singleLogError: error } = useSelector<IStore, {
        singleLog: any;
        singleLogStatus: string | null;
        singleLogError: string | null;
    }>((state) => ({
        singleLog: state.modelFrontLogs.singleLog,
        singleLogStatus: state.modelFrontLogs.singleLogStatus,
        singleLogError: state.modelFrontLogs.singleLogError,
    }));

    useEffect(() => {
        if (logId) {
            dispatch<any>(getModelFrontLogById(Number(logId)));
        }
    }, [logId]);

    const logData = log?.logData;
    const perf = logData?.perf || {};
    const results = logData?.results || [];
    const errors = logData?.errors || [];
    const hasErrors = errors.length > 0;

    if (status === 'loading' || !status) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (status === 'failed') {
        return (
            <PageContainer>
                <Box sx={{ p: 3 }}>
                    <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>
                    <Box sx={{ p: 3, bgcolor: '#ffebee', borderRadius: 2 }}>
                        <Typography color="error" variant="h6">
                            {error || 'Failed to load log'}
                        </Typography>
                    </Box>
                </Box>
            </PageContainer>
        );
    }

    if (!log) return null;

    const totalPerf = (perf.preprocess || 0) + (perf.inference || 0) + (perf.postprocess || 0) + (perf.render || 0);

    return (
        <PageContainer>
            <Box sx={{ p: '20px' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Button startIcon={<ArrowBack />} variant="outlined" size="small" onClick={() => navigate('/model-front-logs')}>
                        Back to list
                    </Button>
                    <Typography variant="h5" component="h1" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                        Log #{logId}
                    </Typography>
                    {hasErrors ? (
                        <Chip icon={<ErrorIcon />} label={`${errors.length} error${errors.length > 1 ? 's' : ''}`} color="error" size="small" />
                    ) : (
                        <Chip icon={<CheckCircle />} label="OK" color="success" size="small" />
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        {(log.imageUrl || logData?.searchImageB64) && (
                            <Button variant="outlined" size="small" startIcon={<ImageIcon />} onClick={() => setImageOpen(true)}>
                                Images
                            </Button>
                        )}
                        <Button variant="outlined" size="small" startIcon={<Code />} onClick={() => setRawOpen(true)}>
                            Raw JSON
                        </Button>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Left column */}
                    <Grid item xs={12} lg={6}>
                        {/* Performance */}
                        <Paper elevation={1} sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                                Performance
                            </Typography>
                            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                <Grid item xs={6} sm={3}><PerfCard label="Preprocess" value={perf.preprocess} /></Grid>
                                <Grid item xs={6} sm={3}><PerfCard label="Inference" value={perf.inference} /></Grid>
                                <Grid item xs={6} sm={3}><PerfCard label="Postprocess" value={perf.postprocess} /></Grid>
                                <Grid item xs={6} sm={3}><PerfCard label="Render" value={perf.render} /></Grid>
                            </Grid>
                            <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Total (sum of stages)</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{totalPerf.toFixed(1)} ms</Typography>
                                </Box>
                                {logData?.searchDurationMs !== undefined && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Search Duration</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{logData.searchDurationMs} ms</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>

                        {/* Session Info */}
                        <Paper elevation={1} sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5 }}>
                                Session Info
                            </Typography>
                            <InfoRow label="Log ID" value={log.id} />
                            <InfoRow label="Session ID" value={logData?.sessionId || '—'} />
                            {logData?.user && (
                                <InfoRow label="User" value={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{logData.user.name}</Typography>
                                        <Chip label={logData.user.role} size="small" variant="outlined" />
                                    </Box>
                                } />
                            )}
                            <InfoRow label="Backend" value={<Chip label={logData?.backend || '—'} size="small" color="primary" variant="outlined" />} />
                            <InfoRow label="Detected Pillows" value={
                                <Chip label={logData?.detectedPillows ?? '—'} size="small" color={logData?.detectedPillows > 0 ? 'success' : 'default'} />
                            } />
                            <InfoRow label="Model Version" value={
                                <Tooltip title={logData?.modelVersion || ''}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 280, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                        {logData?.modelVersion || '—'}
                                    </Typography>
                                </Tooltip>
                            } />
                            {log.createdAt && <InfoRow label="Created At" value={new Date(log.createdAt).toLocaleString()} />}
                        </Paper>

                        {/* Thresholds */}
                        <Paper elevation={1} sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5 }}>
                                Thresholds
                            </Typography>
                            <Grid container spacing={1.5}>
                                {['conf', 'iou', 'mask'].map((key) => (
                                    <Grid item xs={4} key={key}>
                                        <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>{key.toUpperCase()}</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {logData?.thresholds?.[key] ?? '—'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>

                        {/* Errors */}
                        {hasErrors && (
                            <Paper elevation={1} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.error.main}` }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.error.main, mb: 1.5 }}>
                                    Errors ({errors.length})
                                </Typography>
                                {errors.map((err: any, i: number) => (
                                    <Box key={i} sx={{ p: 1.5, bgcolor: '#ffebee', borderRadius: 1, mb: 1 }}>
                                        <Typography variant="body2" color="error">
                                            {typeof err === 'string' ? err : JSON.stringify(err, null, 2)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Paper>
                        )}
                    </Grid>

                    {/* Right column */}
                    <Grid item xs={12} lg={6}>
                        {/* Device */}
                        <Paper elevation={1} sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5 }}>
                                Device
                            </Typography>
                            {log.device ? (() => {
                                const parts = log.device.split('|').map((p: string) => p.trim());
                                const labels = ['User Agent', 'Screen', 'Language', 'Cores'];
                                return parts.map((part: string, i: number) => (
                                    <InfoRow key={i} label={labels[i] || `Part ${i + 1}`} value={part} />
                                ));
                            })() : <Typography variant="body2" color="text.secondary">—</Typography>}
                        </Paper>

                        {/* Results */}
                        <Paper elevation={1} sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5 }}>
                                Search Results ({results.length})
                            </Typography>
                            {results.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No results found.</Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>#</TableCell>
                                                <TableCell>Order Number</TableCell>
                                                <TableCell>Client</TableCell>
                                                <TableCell sx={{ minWidth: 160 }}>Score</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {results.map((r: any, i: number) => (
                                                <TableRow key={i} hover>
                                                    <TableCell sx={{ color: theme.palette.text.secondary }}>{i + 1}</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>{r.orderNumber}</TableCell>
                                                    <TableCell>
                                                        <Chip label={r.client} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <ScoreBar score={r.score} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Images Modal */}
            <Modal open={imageOpen} onClose={() => setImageOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 3, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>Images</Typography>
                        <IconButton onClick={() => setImageOpen(false)}>✕</IconButton>
                    </Box>
                    {log.imageUrl && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Input Image</Typography>
                            <img src={log.imageUrl} alt="Input" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 6, display: 'block' }} />
                        </Box>
                    )}
                    {logData?.searchImageB64 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Search Image</Typography>
                            <img
                                src={`data:image/png;base64,${logData.searchImageB64}`}
                                alt="Search"
                                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 6, display: 'block' }}
                            />
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* Raw JSON Modal */}
            <Modal open={rawOpen} onClose={() => setRawOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 3, maxWidth: '95vw', maxHeight: '95vh', overflow: 'auto', width: 960 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>Raw JSON — Log #{logId}</Typography>
                        <IconButton onClick={() => setRawOpen(false)}>✕</IconButton>
                    </Box>
                    <Box sx={{ bgcolor: theme.palette.action.hover, p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: '80vh', wordBreak: 'break-all' }}>
                        <pre style={{ margin: 0, color: theme.palette.text.primary }}>
                            {JSON.stringify({
                                ...log,
                                logData: {
                                    ...logData,
                                    searchImageB64: logData?.searchImageB64 ? '[truncated — base64]' : undefined,
                                },
                            }, null, 2)}
                        </pre>
                    </Box>
                </Box>
            </Modal>
        </PageContainer>
    );
};

export default OneModelFrontLog;
